// src/pages/maintenance/MaintenanceVendorsPage.js
// Summary list of all maintenance vendors/contractors, showing which ones are also reflected in
// the SQF Hub contractor list (sqfContractorId set) vs. Maintenance-only. See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import maintenanceService from '../../services/maintenanceService';
import { usePermissions } from '../../context/PermissionContext';
import ConfirmModal from '../../components/ConfirmModal';
import MaintenanceNav from '../../components/MaintenanceNav';
import QuickAddContractorDrawer from './QuickAddContractorDrawer';

export default function MaintenanceVendorsPage() {
  const { hasModule } = usePermissions();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importingId, setImportingId] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const sqfEnabled = hasModule('sqf.checklists.manage');

  const load = useCallback(() => {
    setLoading(true);
    maintenanceService.getVendors(sqfEnabled ? { includeSqfContractors: true } : {})
      .then(setVendors)
      .finally(() => setLoading(false));
  }, [sqfEnabled]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async () => {
    await maintenanceService.deleteVendor(archiveTarget.id);
    setArchiveTarget(null);
    load();
  };

  // SQF Hub contractors not yet imported show up in this same list (source: 'sqf_contractor')
  // so adding one here doesn't mean re-entering a contractor already registered in SQF Hub —
  // see VendorPicker.js, which this mirrors.
  const handleImport = async (sqfContractorId) => {
    setImportingId(sqfContractorId);
    try {
      await maintenanceService.importSqfContractor(sqfContractorId);
      load();
    } finally {
      setImportingId(null);
    }
  };

  return (
    <>
      <MaintenanceNav />
      <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Vendors &amp; Contractors</h4>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowDrawer(true); }}>+ New Vendor</button>
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-5 text-muted">No vendors yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Company</th><th>Contact</th><th>Type</th><th>Phone</th><th>Email</th>
                {sqfEnabled && <th>SQF Hub</th>}
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {vendors.map(v => {
                const isSqfOnly = v.source === 'sqf_contractor';
                return (
                  <tr key={isSqfOnly ? `sqf-${v.id}` : v.id} className={isSqfOnly ? 'table-light' : undefined}>
                    <td style={{ fontSize: 13, fontWeight: 600 }}>{v.name}</td>
                    <td style={{ fontSize: 13 }}>{v.contactName || '—'}</td>
                    <td style={{ fontSize: 13 }}>{v.contractorType || '—'}</td>
                    <td style={{ fontSize: 13 }}>{v.contactPhone || '—'}</td>
                    <td style={{ fontSize: 13 }}>{v.contactEmail || '—'}</td>
                    {sqfEnabled && (
                      <td>
                        {isSqfOnly
                          ? <span className="badge bg-info-subtle text-info">🏗 From SQF Hub</span>
                          : v.sqfContractorId
                            ? <span className="badge bg-success-subtle text-success">✓ In SQF Hub</span>
                            : <span className="badge bg-secondary-subtle text-secondary">Maintenance only</span>}
                      </td>
                    )}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="d-flex justify-content-end gap-1">
                        {isSqfOnly ? (
                          <button
                            className="btn btn-sm btn-outline-info"
                            disabled={importingId === v.id}
                            onClick={() => handleImport(v.id)}
                          >
                            {importingId === v.id ? 'Importing…' : 'Import'}
                          </button>
                        ) : (
                          <>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditing(v); setShowDrawer(true); }}>Edit</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => setArchiveTarget(v)}>Archive</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sqfEnabled && (
        <p className="text-muted mt-2" style={{ fontSize: 12 }}>
          Full agreement signing and policy tracking for SQF Hub contractors lives in{' '}
          <Link to="/sqf/contractors">SQF Hub → Contractors</Link>.
        </p>
      )}

      <QuickAddContractorDrawer
        open={showDrawer}
        vendor={editing}
        onClose={() => setShowDrawer(false)}
        onSaved={() => { setShowDrawer(false); setEditing(null); load(); }}
      />

      <ConfirmModal
        show={!!archiveTarget}
        title="Archive Vendor"
        message={`Archive "${archiveTarget?.name}"?`}
        confirmLabel="Archive"
        onConfirm={handleArchive}
        onCancel={() => setArchiveTarget(null)}
      />
      </div>
    </>
  );
}
